import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/*
 * Formatting helpers the admin screens folded in from the api repo use.
 * Carried across from that repo, except for the locale: it arrived as
 * `en-US` and put "Sep 24, 2026, 3:00 PM" in front of operators reading a
 * German product, beside `dd.mm.yyyy` everywhere else in it.
 *
 * (The product has four more date formatters, one per screen family. One
 * shared `formatDate`/`formatDateTime` is the right end state and is a
 * change of its own.)
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${minutes}m ${secs}s`
}

export function formatUptime(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 24) {
    const days = Math.floor(hours / 24)
    return `${days}d ${hours % 24}h`
  }
  return `${hours}h ${minutes}m`
}

export function formatNumber(num: number): string {
  return num.toLocaleString()
}

export function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)

  if (diffSecs < 60) return `${diffSecs} seconds ago`
  if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)} minutes ago`
  if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)} hours ago`
  return `${Math.floor(diffSecs / 86400)} days ago`
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.substring(0, length) + '...'
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    console.error('Failed to copy to clipboard:', err)
    return false
  }
}
